/**
 * PasswordStrength - Visual password strength indicator with 5 bars
 * Matches auth-screens-preview design
 */

import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { colors } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

export interface PasswordStrengthResult {
  score: number;
  label: string;
  color: string;
}

export function getPasswordStrength(password: string): PasswordStrengthResult {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
  if (password.length >= 12) score++;

  let label: string;
  let color: string;
  if (score === 0) {
    label = 'Password strength';
    color = colors.textSecondary;
  } else if (score <= 2) {
    label = 'Weak password';
    color = colors.error;
  } else if (score <= 3) {
    label = 'Medium password';
    color = colors.warning;
  } else {
    label = 'Strong password';
    color = colors.success;
  }

  return { score, label, color };
}

interface PasswordStrengthProps {
  password: string;
}

const BASE_BAR_HEIGHT = 4;
const BASE_LABEL_SIZE = 12;

export function PasswordStrength({ password }: PasswordStrengthProps) {
  const { theme: t, fontScale, fontFamily } = useDisplayTheme();
  const strength = getPasswordStrength(password);
  const barBg = t.border;

  const scaledBarHeight = Math.round(BASE_BAR_HEIGHT * fontScale);
  const fontFamilyStyle = fontFamily ? { fontFamily } : undefined;

  if (password.length === 0) return null;

  return (
    <View style={styles.container}>
      <View style={styles.bars}>
        {[1, 2, 3, 4, 5].map((i) => (
          <View
            key={i}
            style={[
              styles.bar,
              {
                height: scaledBarHeight,
                borderRadius: scaledBarHeight / 2,
                backgroundColor: i <= strength.score ? strength.color : barBg,
              },
            ]}
          />
        ))}
      </View>
      <Text
        allowFontScaling={false}
        style={{
          fontSize: BASE_LABEL_SIZE * fontScale,
          color: strength.color,
          marginTop: 6,
          ...fontFamilyStyle,
        }}
      >
        {strength.label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  bars: {
    flexDirection: 'row',
    gap: 4,
  },
  bar: {
    flex: 1,
  },
});
